import {
  GF_Checkbox,
  GF_CheckboxInput,
  GF_FieldError,
} from '../../../model/gforms/gform';
import useGravityForm, {
  ACTION_TYPES,
  CheckboxFieldValue,
  FieldValue,
} from '../../../hooks/GFormsHooks';
import { useEffect } from 'react';
import { FieldDescription } from './subparts/FieldDescription';
import { FieldDecorator } from './subparts/FieldDecorator';

interface Props {
  field: GF_Checkbox;
  fieldErrors: GF_FieldError[];
}

const defaultValue: GF_CheckboxInput[] = [];

export default function CheckboxField({ field, fieldErrors }: Props) {
  const {
    id,
    formId,
    type,
    label,
    description,
    cssClass,
    inputs,
    choices,
    visibility,
  } = field;
  const checkboxInputs =
    choices?.map((choice, index) => ({ ...choice, id: inputs?.[index]?.id })) ||
    [];
  const htmlId = `field_${formId}_${id}`;
  const { state, dispatch } = useGravityForm();
  const fieldValue = state.find(
    (fieldValue: FieldValue) => fieldValue.id === id
  ) as CheckboxFieldValue | undefined;
  checkboxInputs?.map((choice, index) => {
    if (choice.isSelected) {
      defaultValue[index] = {
        inputId: Number(choice.id),
        value: choice.value,
      };
      return defaultValue[index];
    }
    return defaultValue;
  });
  const checkboxValues =
    fieldValue !== undefined ? fieldValue?.checkboxValues : defaultValue;

  useEffect(() => {
    if (defaultValue.length > 0) {
      dispatch({
        type: ACTION_TYPES.updateCheckboxFieldValue,
        fieldValue: {
          id,
          checkboxValues: defaultValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value, checked } = event.target;
    const otherCheckboxValues = checkboxValues.filter(
      (checkboxValue: GF_CheckboxInput) =>
        checkboxValue.inputId !== Number(name)
    );
    const newCheckboxValues = checked
      ? [...otherCheckboxValues, { inputId: Number(name), value }]
      : otherCheckboxValues;

    dispatch({
      type: ACTION_TYPES.updateCheckboxFieldValue,
      fieldValue: {
        id,
        checkboxValues: newCheckboxValues,
      },
    });
  }

  return (
    <fieldset
      id={htmlId}
      className={`mt-4 md:mt-5 gfield gfield-${type} ${cssClass} ${
        visibility === 'hidden' ? 'hidden' : ''
      }`.trim()}
    >
      <legend className="flex items-center text-black text-sm font-medium mb-3">
        {label}
      </legend>
      <FieldDescription description={description} />
      <FieldDecorator>
        {checkboxInputs.map(
          ({ id: inputId, text, value, isSelected }, index) => {
            const label_array = text?.split('|') || [];
            return (
              <div
                className={`group py-1 px-1.5 rounded-xl flex justify-between hover:bg-primary hover:bg-opacity-10 ${
                  label_array.length > 1 ? 'mb-6' : 'mb-2'
                } last:mb-0`}
                key={inputId}
              >
                <label
                  className={`flex items-center w-full text-black text-sm text-opacity-50 cursor-pointer group-hover:text-primary group-hover:text-opacity-100
              ${
                fieldValue?.checkboxValues?.find(
                  (fieldValue) => fieldValue.value === value
                )
                  ? 'text-opacity-100'
                  : ''
              } `}
                  htmlFor={`input_${formId}_${id}_${inputId}`}
                >
                  <div className="flex flex-col pr-2">
                    {label_array.map((label, index) => {
                      return index === 0 ? (
                        <p key={index} className="font-bold mb-2 last:mb-0">
                          {label}
                        </p>
                      ) : (
                        <p key={index}>{label}</p>
                      );
                    })}
                  </div>
                </label>
                <input
                  className="w-6 h-6 text-primary border-lightGrey focus:ring-transparent rounded-full cursor-pointer"
                  type="checkbox"
                  name={String(inputId)}
                  id={`input_${formId}_${id}_${inputId}`}
                  value={String(value)}
                  onChange={handleChange}
                  defaultChecked={
                    defaultValue?.find(
                      (fieldValue) => fieldValue.value === value
                    ) !== undefined
                  }
                />
              </div>
            );
          }
        )}
      </FieldDecorator>
      {fieldErrors?.length
        ? fieldErrors.map((fieldError) => (
            <p
              key={fieldError.id}
              className="error-message text-error font-medium text-sm mt-2"
            >
              {fieldError.message}
            </p>
          ))
        : null}
    </fieldset>
  );
}
